<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useNavStore } from '@/shared/stores/NavStore'
import { breakpointsTailwind, useBreakpoints, useLocalStorage } from '@vueuse/core'
import { useComposedMenu } from '@/features/dashboard/composables/UseComposedMenu'
import MenuSetupDialog from '@/features/dashboard/components/MenuSetupDialog.vue'
import { useMenuPreferences } from '@/shared/composables/UseMenuPreferences'
import { useAdminAccessStore } from '@/shared/stores/AdminAccessStore'
import SidebarMenu from '@/shared/components/navigation/SidebarMenu.vue'
import MobileMenu from '@/shared/components/navigation/MobileMenu.vue'
import TopBar from '@/shared/components/navigation/TopBar.vue'
import CommandPalette from '@/shared/components/command-palette/CommandPalette.vue'

const route = useRoute()
const navStore = useNavStore()

const { menuItems } = storeToRefs(navStore)

navStore.fill()

/*
 * The sidebar is driven by the user's menu preferences: the always-present Homepage entry first,
 * then the composed category → subcategory groups (or everything as a fallback).
 */
const { sidebarGroups } = useComposedMenu()
const allMenuItems = computed(() => [ ...menuItems.value, ...sidebarGroups.value ])

/*
 * Responsive shell in three modes:
 *  - desktop (≥ lg): full sidebar with labels
 *  - rail (sm–lg): icons-only sidebar that expands over the content on hover
 *  - mobile (< sm): sticky top bar with a bottom-sheet menu
 */
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('sm')
const isRail = breakpoints.between('sm', 'lg')

/*
 * First-run: prompt the user to compose their menu once when nothing is configured yet. The
 * "seen" flag ensures it never reopens (e.g. for someone who intentionally wants to see everything).
 */
const { preferences: menuPreferences } = useMenuPreferences()
const menuSetupSeen = useLocalStorage('s4w:menu-setup-seen', false)
const showMenuSetup = ref(false)
const adminAccess = useAdminAccessStore()
const { loaded: accessLoaded } = storeToRefs(adminAccess)

/*
 * Only prompt once the internal employee's roles are loaded, so the dialog can pre-select their
 * department. `immediate` fires straight away when roles are already loaded (the usual case, since the
 * router guard awaits access.init()); otherwise it waits for the load to complete.
 */
watch(accessLoaded, (loaded) => {
  if (!loaded) {
    return
  }

  const prefs = menuPreferences.value
  const nothingConfigured = prefs.categoryIds.length === 0
    && prefs.subcategoryKeys.length === 0
    && prefs.linkItemIds.length === 0

  if (nothingConfigured && !menuSetupSeen.value) {
    showMenuSetup.value = true
    menuSetupSeen.value = true
  }
}, { immediate: true })
</script>

<template>
  <section class="min-h-screen flex overflow-x-hidden bg-background">
    <MenuSetupDialog v-model:visible="showMenuSetup" />
    <CommandPalette />

    <SidebarMenu v-if="!isMobile" :items="allMenuItems" :collapsed="isRail" />

    <!-- Right column: full-width topbar chrome, centered page content below. -->
    <div class="h-100vh min-w-0 flex flex-1 flex-col">
      <MobileMenu v-if="isMobile" :items="allMenuItems" />

      <TopBar v-if="!route.meta.hideBreadcrumb" />

      <main class="relative mx-auto min-h-0 w-100% flex flex-1 flex-col px-4 sm:px-10">
        <suspense>
          <template #default>
            <!-- py-5 keeps the page content clear of the topbar (and gives the scroll end some room). -->
            <router-view class="min-h-0 flex-1 overflow-hidden overflow-y-auto" :class="route.meta.hideBreadcrumb ? '' : 'py-5'" />
          </template>
          <template #fallback>
            <div class="min-h-0 flex-1 overflow-y-auto p-4 space-y-3">
              <Skeleton height="3rem" />
              <Skeleton height="3rem" />
              <Skeleton height="3rem" />
              <Skeleton height="3rem" />
              <Skeleton height="3rem" />
            </div>
          </template>
        </suspense>
      </main>
    </div>
  </section>
</template>
